import { Component, Input } from '@builder.io/sdk';

import imageField from 'molecules/Image/config';
import sectionFields from 'molecules/Section/config';

/**
 * Reference field that points a component at an entry in the `wistia-forms`
 * Builder data model. Mirrors `formFields` (marketo-forms) so any component can
 * expose a Wistia form selector the same way it exposes a Marketo one.
 */
export const wistiaFormReferenceFields = (showIf?: string) =>
  [
    {
      name: 'form',
      type: 'reference',
      model: 'wistia-forms',
      showIf,
      helperText:
        'Select a Wistia form/live-event entry. Create entries in the "wistia-forms" data model first (each holds the embed type and the Wistia ID).',
    },
  ] as const satisfies DeepReadonly<Input[]>;

/**
 * Field layout for the hosted Wistia form. `grid` keeps two half-width fields
 * per row (First/Last side by side, the Wistia default); `inline` forces every
 * field onto its own row for a single-column form. Applied via a host class the
 * `wistia-form::part(field)` rules in global.css react to.
 */
export const wistiaFormLayoutFields = [
  {
    name: 'formLayout',
    type: 'string',
    enum: ['grid', 'inline'],
    defaultValue: 'grid',
    helperText:
      'Field layout inside the form. "grid" places two fields per row (e.g. First/Last side by side); "inline" stacks every field in a single column.',
  },
] as const satisfies DeepReadonly<Input[]>;

/**
 * Optional chrome rendered around the embed for the bits Wistia can't add
 * inside its hosted form (logo + event date/location line).
 */
export const wistiaEventMetaFields = [
  imageField('logo'),
  {
    name: 'eventDate',
    type: 'string',
    helperText: 'Optional. Shown with a calendar icon above the form (e.g. "September 16, 2026").',
  },
  {
    name: 'eventLocation',
    type: 'string',
    helperText: 'Optional. Shown with a globe icon (e.g. "Virtual Event").',
  },
] as const satisfies DeepReadonly<Input[]>;

/**
 * Optional post-submit redirect (thank-you page). Wired to WistiaFormEmbed's
 * `redirectUrl`: on a successful submission the visitor is sent here, with their
 * UTM params carried onto the URL (appendUtmToUrl) so attribution survives the
 * hop. Use it when the redirect can't be set in Wistia itself (e.g. live-event
 * forms) or when UTMs must persist (Wistia's native redirect drops them).
 * Shared so every Wistia form component exposes the thank-you URL the same way.
 */
export const wistiaRedirectFields = [
  {
    name: 'redirectUrl',
    type: 'string',
    helperText:
      "Optional thank-you URL. After a successful submission the visitor is sent here, with their UTM params carried over automatically. Accepts a full URL or a site path (e.g. /thank-you/...). Leave blank to keep Wistia's default behaviour.",
  },
] as const satisfies DeepReadonly<Input[]>;

const wistiaFormFields = [
  ...wistiaEventMetaFields,
  ...wistiaFormReferenceFields(),
  ...wistiaFormLayoutFields,
  ...wistiaRedirectFields,
  ...sectionFields,
] as const satisfies DeepReadonly<Input[]>;

const wistiaFormConfig = {
  name: 'WistiaForm',
  inputs: wistiaFormFields,
} satisfies Component;

/** Shape of a single entry in the `wistia-forms` Builder data model. */
export type WistiaFormData = {
  name?: string;
  embedType?: 'live-event' | 'form';
  formId?: string;
};

export type WistiaFormReference = {
  model?: string;
  id?: string;
  value?: { data?: WistiaFormData };
};

export type WistiaFormConfigProps = Omit<GenerateBuilder<typeof wistiaFormFields>, 'form'> & {
  form?: WistiaFormReference;
};

export default wistiaFormConfig;
